import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from '@/components/core/DropdownMenu/DropdownMenu';
import { Icon } from '@/components/core/Icon';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { useFiltersService } from '@/pages/browse/hooks/useFiltersService';
import { usePublicationDestinations } from '@/pages/browse/hooks/usePublicationDestinations';
import {
    useValidationErrors,
    useValidationWarnings,
} from '@/pages/browse/hooks/useValidationStatistics';
import { BrowseActiveFilter } from '@/pages/browse/types';
import { useBulkSubscriptions } from '@/pages/bulk-subscriptions/bulk-subscriptions.queries';
import { hasSettingFeature } from '@/services/angularjs-bridge';

import { FilterListActions } from './FilterListActions';
import { FilterListBulkSubscriptions } from './FilterListBulkSubscriptions';
import { FilterListCompliance } from './FilterListCompliance';
import { FilterListPublications } from './FilterListPublications';
import { FilterListSaved } from './FilterListSaved';
import { FilterListValidation } from './FilterListValidation';

export interface FilterDropdownProps {
    activeFilters: Record<string, BrowseActiveFilter>;
    onSelectFilter: (type: string, filter: BrowseActiveFilter) => void;
    onCreateFilter?: () => void;
    onEditFilter?: () => void;
    onDeleteFilter?: () => void;
    /** Optional controlled open state (Storybook / tests). */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export function FilterDropdown({
    activeFilters,
    onSelectFilter,
    onCreateFilter,
    onEditFilter,
    onDeleteFilter,
    open: openProp,
    onOpenChange,
}: FilterDropdownProps) {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const [filterQuery, setFilterQuery] = useState('');
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : uncontrolledOpen;

    const hasRightBulkSubscriptions = Boolean(
        hasSettingFeature('BULK_SUBSCRIPTION')
        && auth?.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'view.bulkSubscriptions')
        && auth?.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_BULK_SUBSCRIPTIONS, 'search'),
    );

    const { data: savedFilters = [] } = useFiltersService();
    const { data: publicationDestinations = [] } = usePublicationDestinations();
    const { data: validationErrors = [] } = useValidationErrors();
    const { data: validationWarnings = [] } = useValidationWarnings();
    const { data: bulkSubscriptionPages } = useBulkSubscriptions({
        variables: { limit: 30, keywords: '', active: true },
        enabled: hasRightBulkSubscriptions,
    });

    const bulkSubscriptions = useMemo(
        () => bulkSubscriptionPages?.pages.flatMap((page) => page.data) ?? [],
        [bulkSubscriptionPages],
    );

    const handleOpenChange = (nextOpen: boolean) => {
        if (!isControlled) {
            setUncontrolledOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
        if (!nextOpen) {
            setFilterQuery('');
        }
    };

    return (
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <ButtonDropdown
                    titleOne={t('ITEMS_SEARCH_FILTER.LABEL')}
                    isOpen={open}
                />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
                <DropdownMenuContent
                    hasBorder
                    hasPadding={false}
                    align="start"
                    className="filter-dropdown tw-min-w-[20em] tw-w-max tw-max-w-[90vw]"
                    onCloseAutoFocus={(event) => event.preventDefault()}
                >
                    <div
                        className="tw-p-2 tw-border-b tw-border-secondary-lighter"
                        onPointerDown={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                    >
                        <div className="tw-flex tw-items-stretch">
                            <span className="tw-flex tw-items-center tw-justify-center tw-px-2 tw-border tw-border-solid tw-border-primary tw-border-r-0 tw-bg-fill-secondary tw-rounded-l">
                                <Icon icon={MagnifyingGlassIcon} size="xs" className="tw-text-t-secondary" />
                            </span>
                            <input
                                type="text"
                                className={
                                    'tw-flex-1 tw-min-w-[12rem] tw-h-9 tw-border tw-border-solid tw-border-primary '
                                    + 'tw-rounded-r tw-px-2 tw-text-t-sm tw-bg-fill-primary tw-outline-none '
                                    + 'focus:tw-border-primary-focus'
                                }
                                value={filterQuery}
                                onChange={(event) => setFilterQuery(event.target.value)}
                                aria-label={t('ITEMS_SEARCH_FILTER.LABEL')}
                                autoFocus
                            />
                        </div>
                    </div>
                    <ul className="scroll-filters tw-list-none tw-m-0 tw-p-0 tw-max-h-[345px] tw-overflow-y-auto tw-w-max tw-min-w-full">
                        <FilterListActions
                            activeFilters={activeFilters}
                            onCreateFilter={onCreateFilter}
                            onEditFilter={onEditFilter}
                            onDeleteFilter={onDeleteFilter}
                        />
                        <FilterListSaved
                            filters={savedFilters}
                            query={filterQuery}
                            activeFilters={activeFilters}
                            onSelect={onSelectFilter}
                        />
                        <FilterListPublications
                            destinations={publicationDestinations}
                            query={filterQuery}
                            onSelect={onSelectFilter}
                        />
                        <FilterListCompliance query={filterQuery} onSelect={onSelectFilter} />
                        <FilterListValidation
                            section="errors"
                            errors={validationErrors}
                            query={filterQuery}
                            onSelect={onSelectFilter}
                        />
                        {hasRightBulkSubscriptions && (
                            <FilterListBulkSubscriptions
                                subscriptions={bulkSubscriptions}
                                query={filterQuery}
                                onSelect={onSelectFilter}
                            />
                        )}
                        <li
                            role="separator"
                            className="tw-h-px tw-bg-secondary-lighter tw-my-1 tw-list-none"
                        />
                        <FilterListValidation
                            section="warnings"
                            warnings={validationWarnings}
                            query={filterQuery}
                            onSelect={onSelectFilter}
                        />
                    </ul>
                </DropdownMenuContent>
            </DropdownMenuPortal>
        </DropdownMenu>
    );
}
